<template>
  <div>
    <ReportItemContainer v-if="isReportMode">
      <template #question>{{ question.text }}</template>
      <template #answer>
        <div v-for="item in certificates" :key="item.label" class="mb-3">
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("certificate") }}:
            </span>
            {{ item.label }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("checked") }}:
            </span>
            {{ getAnswerText(item.checked) }}
          </p>
          <div v-if="item.checked">
            <p>
              <span class="report-item-container__label"
                >{{ getLabel("expiryDate") }}:
              </span>
              {{ item.expiryDate }}
            </p>
            <p>
              <span class="report-item-container__label"
                >{{ getLabel("dateLastAudit") }}:
              </span>
              {{ item.auditDate }}
            </p>
            <p>
              <span class="report-item-container__label"
                >{{ getLabel("resultsLastAudit") }}:
              </span>
              {{ item.auditResult.text }}
            </p>
          </div>
        </div>
      </template>
    </ReportItemContainer>
    <div v-else>
      <p class="mb-4">
        {{ question.text }}
      </p>
      <div v-for="item in certificates" :key="item.label">
        <v-row>
          <v-col cols="12" sm="6" md="12">
            <div class="d-flex align-center">
              <PCheckBox
                :label="item.label"
                :value="item.checked"
                :disabled="isReadOnlyMode"
                @value-changed="(state) => certificateSelected(item, state)"
              />
              <InfoPopup
                v-if="item.details != undefined && item.details.length > 0"
                :title="item.label"
                :content="item.details"
              />
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <DateInput
              :label="getLabel('expiryDate')"
              :value="item.expiryDate"
              :disabled="!item.checked"
              @updateEvent="(val) => (item.expiryDate = val)"
              @valid="(val) => emitValid(val, 'expireDate')"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <DateInput
              :label="getLabel('dateLastAudit')"
              :value="item.auditDate"
              :disabled="!item.checked"
              @updateEvent="(val) => (item.auditDate = val)"
              @valid="(val) => emitValid(val, 'dateLastAudit')"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <PAutocomplete
              :items="auditResults"
              :select-text="item.checked ? item.auditResult.id : undefined"
              :label="getLabel('resultsLastAudit')"
              :disabled="!item.checked"
              :clearable="true"
              @field-updated="(val: string) => auditResultsSelected(item, val)"
            />
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type Ref } from "vue";
import DateInput from "@/components/custom/DateInput.vue";
import {
  AnswerCertificateModel,
  CertificateModel,
} from "@/models/questions/certificateModel";
import ReportItemContainer from "@/components/containers/ReportItemContainer.vue";
import InfoPopup from "@/components/custom/InfoPopup.vue";
import PCheckBox from "@/components/custom/PCheckBox.vue";
import type { QuestionModelDto } from "@/models/dto/questionModelDto";
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";
import { QuestionnaireTemplateCommonItemsId } from "@/models/enum/questionnaireTemplateCommonItemsId";
import type { ItemTextDto } from "@/models/dto/questionDto";
import PAutocomplete from "@/components/custom/PAutocomplete.vue";

const props = withDefaults(
  defineProps<{
    questionModel: QuestionModelDto;
    answer?: AnswerCertificateModel[] | null;
    isReportMode?: boolean;
    isReadOnlyMode?: boolean;
  }>(),
  {
    isReportMode: false,
    isReadOnlyMode: false,
  }
);

const emit = defineEmits<{
  (e: "qEvent", newValue: AnswerCertificateModel[]): void;
  (e: "isValid", isValid: boolean, invalidField: string): void;
}>();

const selectedQuestionnaireStore = useSelectedQuestionnaire();
const checkBoxAnswerDisplayText = selectedQuestionnaireStore.getCommonItems(
  QuestionnaireTemplateCommonItemsId.Checkbox
);

const certificateDisplayText = selectedQuestionnaireStore.getCommonItems(
  QuestionnaireTemplateCommonItemsId.Certificates
);

const auditResults = selectedQuestionnaireStore.getCommonItems(
  QuestionnaireTemplateCommonItemsId.AuditResult
);

const question = computed(() => props.questionModel.question);
const certificates: Ref<CertificateModel[]> = ref(getCertificates());

function getCertificates(): CertificateModel[] {
  if (
    question.value.itemsWithDetails == undefined ||
    question.value.itemsWithDetails.length == 0
  ) {
    return new Array<CertificateModel>();
  }

  const answersExist = props.answer != undefined && props.answer.length > 0;

  if (!answersExist) {
    return question.value.itemsWithDetails!.map(
      (item: ItemTextDto) => new CertificateModel(item.id, item.text)
    );
  }

  return question.value.itemsWithDetails!.map((item: ItemTextDto) => {
    const model = new CertificateModel(item.id, item.text);
    const answer = props.answer!.find(
      (a: AnswerCertificateModel) => a.label === item.id
    );
    if (answer) {
      model.expiryDate = answer.expiryDate;
      model.auditDate = answer.auditDate;
      model.checked = answer.checked;
      model.auditResult = auditResults.find(
        (x) => x.id == answer.auditResult
      ) ?? { id: "", text: "" };
    }
    return model;
  });
}

function emitNewCertificates(): void {
  const answerCertificateModels = certificates.value.map((x) =>
    x.toAnswerModel()
  );
  emit("qEvent", answerCertificateModels);
}

function emitValid(isValid: boolean, fieldId: string): void {
  emit("isValid", isValid, `${question.value.id}__${fieldId}`);
}

function certificateSelected(item: CertificateModel, state: boolean) {
  item.setChecked(state);
  emitNewCertificates();
}

function auditResultsSelected(item: CertificateModel, result: string) {
  item.auditResult =
    auditResults.find((x) => x.id == result) ?? ({} as ItemTextDto);
  emitNewCertificates();
}

function getAnswerText(answerId: boolean): string {
  const entry = checkBoxAnswerDisplayText.find(
    (x) => x.id == answerId.toString()
  );
  return entry?.text ?? "";
}

function getLabel(id: string): string {
  return certificateDisplayText.find((x) => x.id == id)?.text ?? "";
}
</script>
